import React, { useEffect, useState } from 'react';
import { RichTextContent } from '../shared/RichTextEditor';
import { fetchPublicLegal, fetchUserLegal, type PublicLegalView } from './legalApi';
import {
  LEGAL_TEMPLATE_NON_ATTORNEY,
  LEGAL_TEMPLATE_REFERRER_DISCLOSURE,
} from '../../lib/legalContentTemplates';

/**
 * Disclosure components (Slice D D-05 / D-11).
 *
 * Text comes from the workspace Legal config; when the workspace has not
 * configured its own text yet, the attorney-review-flagged MODEL wording
 * renders (it is a disclaimer/disclosure, never an amount or a promise).
 * The referrer disclosure states existence and nature only — commission
 * AMOUNTS are never shown to applicants (standing product rule).
 *
 * ⚠️ Model wording: ATTORNEY REVIEW REQUIRED before production (see
 * src/lib/legalContentTemplates.ts).
 */
/**
 * Legal content for disclosures: the signed-in user's own workspace when
 * it has content (exact), otherwise the host-resolved public view (the
 * only source on unauthenticated pages like payment/quote links). Both
 * are the same workspace in the normal single-workspace-per-host setup.
 */
function useUserLegal() {
  const [view, setView] = useState<PublicLegalView | null>(null);
  useEffect(() => {
    let alive = true;
    Promise.all([fetchUserLegal(), fetchPublicLegal()]).then(([user, pub]) => {
      if (!alive) return;
      const userHasContent = user.configured || Object.keys(user.disclosures).length > 0 || Object.keys(user.documents).length > 0;
      setView(userHasContent ? user : pub);
    });
    return () => { alive = false; };
  }, []);
  return view;
}

export function NonAttorneyDisclaimer({ className = '' }: { className?: string }) {
  const view = useUserLegal();
  if (!view) return null;
  const html = view.disclosures.nonAttorneyDisclaimer?.html || LEGAL_TEMPLATE_NON_ATTORNEY;
  return (
    <div
      className={`bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-4 py-3 text-xs leading-relaxed ${className}`}
      role="note"
    >
      <RichTextContent html={html} />
    </div>
  );
}

export function ReferrerCommissionDisclosure({ className = '' }: { className?: string }) {
  const view = useUserLegal();
  if (!view) return null;
  const html = view.disclosures.referrerCommissionDisclosure?.html || LEGAL_TEMPLATE_REFERRER_DISCLOSURE;
  return (
    <div
      className={`bg-slate-50 border border-slate-200 text-slate-700 rounded-xl px-4 py-3 text-xs leading-relaxed ${className}`}
      role="note"
    >
      <RichTextContent html={html} />
    </div>
  );
}
